const RELEASE_GATE_BYPASS_QUERY_PARAM = 'release_bypass';
const RELEASE_GATE_BYPASS_STORAGE_KEY = 'acceliqa.releaseGateBypass';

function readEnv(name: keyof RuntimeEnv): string {
  if (typeof window !== 'undefined' && window._env_?.[name]) {
    return String(window._env_[name] || '').trim();
  }

  const envValue = process.env[name];
  return String(envValue || '').trim();
}

export function getReleaseGateHosts(): string[] {
  const rawHosts = readEnv('REACT_APP_RELEASE_GATE_HOST');
  if (!rawHosts) {
    return [];
  }

  return rawHosts
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
}

export function getReleaseAt(): Date | null {
  const rawReleaseAt = readEnv('REACT_APP_RELEASE_AT');
  if (!rawReleaseAt) {
    return null;
  }

  const parsed = new Date(rawReleaseAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function getReleaseGateBypassToken(): string {
  return readEnv('REACT_APP_RELEASE_GATE_BYPASS_TOKEN');
}

function removeReleaseGateBypassParam(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(RELEASE_GATE_BYPASS_QUERY_PARAM);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // Ignore URL cleanup failures; the bypass state has already been handled.
  }
}

export function isReleaseGateBypassActive(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return (
      window.localStorage.getItem(RELEASE_GATE_BYPASS_STORAGE_KEY) === 'true'
      || window.sessionStorage.getItem(RELEASE_GATE_BYPASS_STORAGE_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

export function applyReleaseGateBypassFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  let bypassValue: string | null = null;
  try {
    const url = new URL(window.location.href);
    bypassValue = url.searchParams.get(RELEASE_GATE_BYPASS_QUERY_PARAM);
  } catch {
    return isReleaseGateBypassActive();
  }

  if (bypassValue === null) {
    return isReleaseGateBypassActive();
  }

  const normalizedBypassValue = bypassValue.trim();
  if (normalizedBypassValue.toLowerCase() === 'clear') {
    try {
      window.localStorage.removeItem(RELEASE_GATE_BYPASS_STORAGE_KEY);
      window.sessionStorage.removeItem(RELEASE_GATE_BYPASS_STORAGE_KEY);
    } catch {
      // Ignore storage failures and continue URL cleanup.
    }
    removeReleaseGateBypassParam();
    return false;
  }

  const expectedToken = getReleaseGateBypassToken();
  if (expectedToken && normalizedBypassValue === expectedToken) {
    try {
      window.localStorage.setItem(RELEASE_GATE_BYPASS_STORAGE_KEY, 'true');
    } catch {
      try {
        window.sessionStorage.setItem(RELEASE_GATE_BYPASS_STORAGE_KEY, 'true');
      } catch {
        // If storage is unavailable, allow this page load but do not persist it.
        removeReleaseGateBypassParam();
        return true;
      }
    }
    removeReleaseGateBypassParam();
    return true;
  }

  removeReleaseGateBypassParam();
  return isReleaseGateBypassActive();
}

export function isReleaseGateActive(currentHostname?: string): boolean {
  if (applyReleaseGateBypassFromUrl()) {
    return false;
  }

  const releaseAt = getReleaseAt();
  const hosts = getReleaseGateHosts();

  if (!releaseAt || hosts.length === 0 || typeof window === 'undefined') {
    return false;
  }

  const hostname = String(currentHostname || window.location.hostname || '').trim().toLowerCase();
  if (!hostname || !hosts.includes(hostname)) {
    return false;
  }

  return Date.now() < releaseAt.getTime();
}

export function getReleaseDisplayLabel(): string {
  const releaseAt = getReleaseAt();
  if (!releaseAt) {
    return 'Coming soon';
  }

  const configuredTimeZone = readEnv('REACT_APP_RELEASE_TIME_ZONE') || undefined;

  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: configuredTimeZone,
      timeZoneName: 'short',
    }).format(releaseAt);
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(releaseAt);
  }
}
